/**
 * W5 — Command palette registry
 */

import type { PathPlane } from '@shared/tenant-host/pathPlanes';
import type { UserRole } from '@shared/types/roles';
import { resolveDomainActionFamily } from './domain-action-rule-table';
import type { CommandPaletteGroup } from './workbench-command-palette-contract';

/** Priority boost when active workbench domain matches command seed. */
export const COMMAND_CONTEXT_DOMAIN_PRIORITY_BOOST = 25;

export type CommandKind = 'navigate' | 'action' | 'search' | 'explain';

export type { CommandPaletteGroup };

export interface CommandEntry {
  id: string;
  label: string;
  keywords?: string[];
  kind: CommandKind;
  route?: string;
  actionRef?: string;
  planes?: PathPlane[];
  roles?: UserRole[];
  priority?: number;
  /** WB4-CP1 — palette section override */
  paletteGroup?: CommandPaletteGroup;
  /** WB4-CP — contextual ranking: boost when focus domain matches */
  businessDomains?: readonly string[];
  /** Explain kind — source kernel action id for handlerRef derivation */
  sourceActionId?: string;
}

const commandEntries: CommandEntry[] = [];

export function registerCommands(entries: CommandEntry[]): void {
  for (const entry of entries) {
    if (!commandEntries.some((existing) => existing.id === entry.id)) {
      commandEntries.push(entry);
    }
  }
}

export function clearCommandRegistry(): void {
  commandEntries.length = 0;
}

export function getRegisteredCommands(): readonly CommandEntry[] {
  return commandEntries;
}

function commandMatchesActiveDomain(entry: CommandEntry, activeBusinessDomain: string): boolean {
  if (!entry.businessDomains?.length) {
    return false;
  }
  if (entry.businessDomains.includes(activeBusinessDomain)) {
    return true;
  }
  const activeFamily = resolveDomainActionFamily(activeBusinessDomain);
  if (!activeFamily) {
    return false;
  }
  return entry.businessDomains.some((domain) => resolveDomainActionFamily(domain) === activeFamily);
}

export function applyCommandContextPriority(
  entries: readonly CommandEntry[],
  activeBusinessDomain?: string
): CommandEntry[] {
  if (!activeBusinessDomain?.trim()) {
    return [...entries];
  }
  const normalizedDomain = activeBusinessDomain.trim();
  return entries.map((entry) => {
    if (!commandMatchesActiveDomain(entry, normalizedDomain)) {
      return entry;
    }
    return {
      ...entry,
      priority: (entry.priority ?? 0) + COMMAND_CONTEXT_DOMAIN_PRIORITY_BOOST,
    };
  });
}

export function filterCommands(input: {
  query: string;
  plane: PathPlane;
  role: UserRole;
  activeBusinessDomain?: string;
}): CommandEntry[] {
  const normalized = input.query.trim().toLowerCase();
  const filtered = commandEntries.filter((entry) => {
    if (entry.planes && !entry.planes.includes(input.plane)) {
      return false;
    }
    if (entry.roles && !entry.roles.includes(input.role)) {
      return false;
    }
    if (!normalized) {
      return true;
    }
    const haystack = [entry.label, ...(entry.keywords ?? [])].join(' ').toLowerCase();
    return haystack.includes(normalized);
  });
  return applyCommandContextPriority(filtered, input.activeBusinessDomain).sort(
    (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
  );
}
